import { useState, useRef, Fragment } from 'react';
import { z } from 'zod';
import { BackButton } from "@lecartte/shared-ui/components/RoundIconButton";
import type { DiscountInfo } from '@/features/subscription/components/PromoCodeInput';

export const emptyCard = { holderName: '', number: '', expiryMonth: '', expiryYear: '', ccv: '' };
export const emptyHolderInfo = { name: '', email: '', cpfCnpj: '', mobilePhone: '', postalCode: '', addressNumber: '' };

export const formatPrice = ({ valueCents }: { valueCents: number }) => {
  if (valueCents === 0) return 'Grátis';
  return `R$ ${(valueCents / 100).toFixed(2).replace('.', ',')}/mês`;
};

const maskCep = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

export const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length > 10) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
  if (d.length > 6) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length > 2) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return d.length > 0 ? `(${d}` : d;
};

// WhatsApp: +CC (DDD) XXXXX-XXXX (13 digits) or +CC (DDD) XXXX-XXXX (12 digits)
export const maskWhatsApp = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 13);
  if (d.length === 0) return '';
  if (d.length <= 2) return `+${d}`;
  if (d.length <= 4) return `+${d.slice(0, 2)} (${d.slice(2)}`;
  if (d.length <= 8) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4)}`;
  if (d.length <= 12) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
};

const maskCardNumber = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 16);
  return d.replace(/(\d{4})(?=\d)/g, '$1 ');
};

export const isValidCpf = (digits: string) => {
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
  const calc = (len: number) => {
    const sum = digits.slice(0, len).split('').reduce((acc, d, i) => acc + Number(d) * (len + 1 - i), 0);
    const rem = (sum * 10) % 11;
    return rem === 10 ? 0 : rem;
  };
  return calc(9) === Number(digits[9]) && calc(10) === Number(digits[10]);
};

// New alphanumeric CNPJ format (IN RFB nº 2.229/2024, effective July 2026).
// First 12 chars: A-Z or 0-9. Last 2: numeric check digits.
// Character value for the algorithm = charCode - 48 (same as before for digits;
// 'A'=17, 'B'=18, ..., 'Z'=42). Existing all-numeric CNPJs remain valid.
export const isValidCnpj = (v: string) => {
  if (v.length !== 14) return false;
  if (!/^[A-Z0-9]{12}\d{2}$/.test(v)) return false;
  if (/^(.)\1{13}$/.test(v)) return false;
  const charVal = (c: string) => c.charCodeAt(0) - 48;
  const calc = (len: number, weights: number[]) => {
    const sum = v.slice(0, len).split('').reduce((acc, c, i) => acc + charVal(c) * weights[i], 0);
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  return calc(12, w1) === Number(v[12]) && calc(13, w2) === Number(v[13]);
};

// Applies CPF (XXX.XXX.XXX-DD) or CNPJ (XX.XXX.XXX/XXXX-DD) mask.
// v is the raw alphanumeric value without separators, already uppercase.
export const maskCpfCnpj = (v: string) => {
  if (v.length <= 11) {
    // CPF — digits only
    return v
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  // CNPJ — alphanumeric: XX.XXX.XXX/XXXX-DD
  const s = (a: number, b: number) => v.slice(a, b);
  let r = s(0, 2);
  if (v.length > 2) r += '.' + s(2, 5);
  if (v.length > 5) r += '.' + s(5, 8);
  if (v.length > 8) r += '/' + s(8, 12);
  if (v.length > 12) r += '-' + s(12, 14);
  return r;
};

export const holderInfoSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  email: z.string().email('Email inválido'),
  cpfCnpj: z.string()
    .regex(/^\d{11}$|^[A-Z0-9]{12}\d{2}$/, 'CPF: 11 dígitos · CNPJ: 12 caracteres alfanuméricos + 2 dígitos verificadores')
    .refine((v) => v.length === 11 ? isValidCpf(v) : isValidCnpj(v), 'CPF/CNPJ inválido'),
  mobilePhone: z.string().regex(/^\d{11}$/, 'Celular deve ter 11 dígitos (DDD + 9 dígitos)'),
  postalCode: z.string().regex(/^\d{8}$/, 'CEP deve ter 8 dígitos'),
  addressNumber: z.string().min(1, 'Número é obrigatório'),
});

export const cardSchema = z.object({
  holderName: z.string().min(3, 'Nome do titular é obrigatório'),
  number: z.string().regex(/^\d{16}$/, 'Número do cartão deve ter 16 dígitos'),
  expiryMonth: z.string().regex(/^(0[1-9]|1[0-2])$/, 'Mês inválido (01-12)'),
  expiryYear: z.string().regex(/^\d{2}$/, 'Ano inválido (ex: 34)'),
  ccv: z.string().regex(/^\d{3,4}$/, 'CVV deve ter 3 ou 4 dígitos'),
});

type CardSubStep = 'holder_info' | 'card_data' | 'confirm';

const CARD_SUB_STEPS: Array<{ id: CardSubStep; label: string }> = [
  { id: 'holder_info', label: 'Titular' },
  { id: 'card_data', label: 'Cartão' },
  { id: 'confirm', label: 'Confirmar' },
];

const getCardNetwork = ({ num }: { num: string }) => {
  if (/^4/.test(num)) return 'visa';
  if (/^5[1-5]/.test(num) || /^2[2-7]\d{2}/.test(num)) return 'mastercard';
  if (/^3[47]/.test(num)) return 'amex';
  if (/^(606282|3841|636368|438935|504175|451416|636297)/.test(num)) return 'hipercard';
  if (/^(4011|4312|4389|4514|4576|5041|5066|5090|6277|6362|6363|650[05]|6516|6550)/.test(num)) return 'elo';
  return null;
};

const NetworkLogo = ({ network }: { network: string | null }) => {
  if (network === 'visa') return (
    <svg viewBox="0 0 80 26" className="h-6 w-auto" fill="white">
      <path d="M30.6 0.8L19.9 25.2H12.8L7.5 6.3C7.2 5.1 6.9 4.7 6 4.2 4.5 3.4 2 2.6 0 2.1L0.2 0.8H11.6C13.1 0.8 14.4 1.8 14.7 3.4L17.5 18.5 24.6 0.8H30.6ZM55.3 17.2C55.3 11.2 47 10.9 47.1 8.2 47.1 7.4 47.9 6.5 49.6 6.3 50.5 6.2 52.9 6.1 55.6 7.4L56.7 2.3C55.2 1.7 53.3 1.2 51 1.2 45.4 1.2 41.5 4.2 41.4 8.5 41.4 11.7 44.3 13.5 46.5 14.6 48.8 15.7 49.5 16.4 49.5 17.4 49.5 19 47.6 19.6 45.8 19.6 42.8 19.7 41.1 18.8 39.7 18.2L38.6 23.5C40.1 24.1 42.9 24.7 45.8 24.7 51.8 24.7 55.5 21.7 55.3 17.2ZM69.8 25.2H75.2L70.4 0.8H65.1C63.8 0.8 62.7 1.6 62.2 2.8L53.5 25.2H59.5L60.7 21.9H67.9L68.9 25.2H69.8ZM62.4 17.1L65.3 9.2 67 17.1H62.4ZM38.5 0.8L33.6 25.2H27.9L32.8 0.8H38.5Z" />
    </svg>
  );
  if (network === 'mastercard') return (
    <svg viewBox="0 0 38 24" className="h-7 w-auto">
      <circle cx="14" cy="12" r="12" fill="#EB001B" />
      <circle cx="24" cy="12" r="12" fill="#F79E1B" />
      <path d="M19 5.3A12 12 0 0 1 23.7 12 12 0 0 1 19 18.7 12 12 0 0 1 14.3 12 12 0 0 1 19 5.3Z" fill="#FF5F00" />
    </svg>
  );
  if (network === 'amex') return (
    <svg viewBox="0 0 56 20" className="h-5 w-auto" fill="white">
      <text x="0" y="16" fontSize="16" fontWeight="bold" fontFamily="Arial, sans-serif" letterSpacing="1">AMEX</text>
    </svg>
  );
  if (network === 'hipercard') return (
    <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-white">HIPER</span>
  );
  if (network === 'elo') return (
    <span className="rounded bg-yellow-400 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-slate-900">ELO</span>
  );
  return null;
};

export const CardPaymentForm = ({
  planName,
  valueCents,
  card,
  setCard,
  holderInfo,
  setHolderInfo,
  error,
  isPending,
  onBack,
  onSubmit,
  wide = false,
  mode = 'subscribe',
  submitLabel,
  footer,
  discount,
  promoCodeSlot,
}: {
  planName: string;
  valueCents: number;
  card: typeof emptyCard;
  setCard: React.Dispatch<React.SetStateAction<typeof emptyCard>>;
  holderInfo: typeof emptyHolderInfo;
  setHolderInfo: React.Dispatch<React.SetStateAction<typeof emptyHolderInfo>>;
  error: string | null;
  isPending: boolean;
  onBack: () => void;
  onSubmit: () => void;
  wide?: boolean;
  mode?: 'subscribe' | 'save_card';
  submitLabel?: string;
  footer?: React.ReactNode;
  discount?: DiscountInfo | null;
  promoCodeSlot?: React.ReactNode;
}) => {
  const [subStep, setSubStep] = useState<CardSubStep>('holder_info');
  const [holderErrors, setHolderErrors] = useState<Record<string, string>>({});
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [shakeKey, setShakeKey] = useState<number>(0);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const ccvRef = useRef<HTMLInputElement>(null);
  const stepIndex = CARD_SUB_STEPS.findIndex((s) => s.id === subStep);
  const network = getCardNetwork({ num: card.number });

  const validateHolderInfo = () => {
    const result = holderInfoSchema.safeParse(holderInfo);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0];
        if (typeof key === 'string') errors[key] = issue.message;
      });
      setHolderErrors(errors);
      setShakeKey((k) => k + 1);
      return false;
    }
    setHolderErrors({});
    return true;
  };

  const validateCard = () => {
    const result = cardSchema.safeParse(card);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0];
        if (typeof key === 'string') errors[key] = issue.message;
      });
      setCardErrors(errors);
      setShakeKey((k) => k + 1);
      return false;
    }
    setCardErrors({});
    return true;
  };

  const isStepValid = (stepId: CardSubStep) => {
    if (stepId === 'holder_info') return true;
    if (stepId === 'card_data') return holderInfoSchema.safeParse(holderInfo).success;
    if (stepId === 'confirm') {
      return holderInfoSchema.safeParse(holderInfo).success && cardSchema.safeParse(card).success;
    }
    return false;
  };

  const handleStepChange = (stepId: CardSubStep) => {
    const currentIndex = CARD_SUB_STEPS.findIndex((s) => s.id === subStep);
    const targetIndex = CARD_SUB_STEPS.findIndex((s) => s.id === stepId);
    if (targetIndex < currentIndex) { setSubStep(stepId); return; }
    if (targetIndex > currentIndex) {
      if (subStep === 'holder_info' && !validateHolderInfo()) return;
      if (subStep === 'card_data' && !validateCard()) return;
    }
    setSubStep(stepId);
  };

  const bubbleSize = wide ? 'h-9 w-9 text-sm' : 'h-7 w-7 text-xs';
  const labelSize = wide ? 'text-sm' : 'text-xs';

  const getStepBubbleClass = (i: number) =>
    i > stepIndex
      ? `flex ${bubbleSize} items-center justify-center rounded-full font-bold transition-all bg-slate-200 text-slate-400`
      : `flex ${bubbleSize} items-center justify-center rounded-full font-bold transition-all`;

  const getStepBubbleStyle = (i: number): React.CSSProperties =>
    i <= stepIndex
      ? { backgroundColor: 'var(--color-secondary)', color: 'white', boxShadow: i === stepIndex ? '0 0 0 4px var(--color-secondary-soft)' : undefined }
      : {};

  const getStepLabelClass = (i: number) =>
    i <= stepIndex ? `${labelSize} font-medium text-secondary` : `${labelSize} font-medium text-slate-400`;

  const getStepConnectorStyle = (i: number): React.CSSProperties => ({
    backgroundColor: i < stepIndex ? 'var(--color-secondary)' : '#e2e8f0',
  });

  const getStepButtonClass = ({ valid, isForward }: { valid: boolean; isForward: boolean }) =>
    `flex flex-col items-center gap-1 ${!valid && isForward ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`;

  const getInputClass = ({ extra, error }: { extra?: string; error: string }) => {
    const base = extra ? `input w-full ${extra}` : 'input w-full';
    return error ? `${base} border-red-300 focus:border-red-300 focus:ring-red-100 animate-shake` : base;
  };

  const formatDisplayNumber = () => {
    const raw = card.number.padEnd(16, '•');
    return `${raw.slice(0, 4)}  ${raw.slice(4, 8)}  ${raw.slice(8, 12)}  ${raw.slice(12, 16)}`;
  };

  const goBack = () => {
    if (subStep === 'holder_info') onBack();
    else setSubStep(CARD_SUB_STEPS[stepIndex - 1].id);
  };

  const cardPreview = (
    <div
      className="relative h-44 w-full overflow-hidden rounded-2xl p-5 text-white shadow-xl select-none"
      style={{ background: 'linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-strong) 60%, #8b1e14 100%)' }}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -right-3 top-14 h-32 w-32 rounded-full bg-white/5" />
      <div className="pointer-events-none absolute -left-6 -bottom-6 h-32 w-32 rounded-full bg-white/5" />

      <div className="flex items-start justify-between">
        <svg className="h-8 w-10" viewBox="0 0 40 30" fill="none">
          <rect width="40" height="30" rx="5" fill="#D4AF37" />
          <rect x="13" width="14" height="30" fill="#B8971E" opacity="0.5" />
          <rect y="10" width="40" height="10" fill="#B8971E" opacity="0.5" />
          <rect x="13" y="10" width="14" height="10" fill="#C9A227" opacity="0.8" />
        </svg>
        <div className="flex items-center">
          <NetworkLogo network={network} />
        </div>
      </div>

      <p className="absolute bottom-14 left-5 font-mono text-base tracking-[0.22em] text-white/90">
        {formatDisplayNumber()}
      </p>
      <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
        <div className="max-w-[60%] overflow-hidden">
          <p className="text-[10px] uppercase tracking-widest text-white/50">Titular</p>
          <p className="truncate text-sm font-medium uppercase">{card.holderName || '•••••••••••'}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest text-white/50">Validade</p>
          <p className="text-sm font-medium">
            {card.expiryMonth ? card.expiryMonth.padStart(2, '0') : 'MM'}/{card.expiryYear ? card.expiryYear.slice(-2) : 'AA'}
          </p>
        </div>
      </div>
    </div>
  );

  const holderInfoFields = (
    <>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Nome completo</label>
          <input
            key={`name-${shakeKey}`}
            data-testid="holder-name"
            className={getInputClass({ error: holderErrors.name })}
            placeholder="Seu nome completo"
            value={holderInfo.name}
            onChange={(e) => {
              setHolderInfo((p) => ({ ...p, name: e.target.value }));
              if (holderErrors.name) setHolderErrors((prev) => ({ ...prev, name: '' }));
            }}
          />
          {holderErrors.name && <p className="mt-1 text-xs text-red-800">{holderErrors.name}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">E-mail</label>
          <input
            key={`email-${shakeKey}`}
            data-testid="holder-email"
            className={getInputClass({ error: holderErrors.email })}
            placeholder="seu@email.com"
            type="email"
            value={holderInfo.email}
            onChange={(e) => {
              setHolderInfo((p) => ({ ...p, email: e.target.value }));
              if (holderErrors.email) setHolderErrors((prev) => ({ ...prev, email: '' }));
            }}
          />
          {holderErrors.email && <p className="mt-1 text-xs text-red-800">{holderErrors.email}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">CPF/CNPJ</label>
          <input
            key={`cpfCnpj-${shakeKey}`}
            data-testid="holder-cpf-cnpj"
            className={getInputClass({ error: holderErrors.cpfCnpj })}
            placeholder="000.000.000-00"
            value={maskCpfCnpj(holderInfo.cpfCnpj)}
            onChange={(e) => {
              setHolderInfo((p) => ({ ...p, cpfCnpj: e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 14) }));
              if (holderErrors.cpfCnpj) setHolderErrors((prev) => ({ ...prev, cpfCnpj: '' }));
            }}
            maxLength={18}
          />
          {holderErrors.cpfCnpj && <p className="mt-1 text-xs text-red-800">{holderErrors.cpfCnpj}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Celular</label>
          <input
            key={`mobilePhone-${shakeKey}`}
            data-testid="holder-mobile-phone"
            className={getInputClass({ error: holderErrors.mobilePhone })}
            placeholder="(11) 99999-9999"
            value={maskPhone(holderInfo.mobilePhone)}
            onChange={(e) => {
              setHolderInfo((p) => ({ ...p, mobilePhone: e.target.value.replace(/\D/g, '').slice(0, 11) }));
              if (holderErrors.mobilePhone) setHolderErrors((prev) => ({ ...prev, mobilePhone: '' }));
            }}
            maxLength={15}
          />
          {holderErrors.mobilePhone && <p className="mt-1 text-xs text-red-800">{holderErrors.mobilePhone}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">CEP</label>
            <input
              key={`postalCode-${shakeKey}`}
              data-testid="holder-postal-code"
              className={getInputClass({ error: holderErrors.postalCode })}
              placeholder="00000-000"
              value={maskCep(holderInfo.postalCode)}
              onChange={(e) => {
                setHolderInfo((p) => ({ ...p, postalCode: e.target.value.replace(/\D/g, '').slice(0, 8) }));
                if (holderErrors.postalCode) setHolderErrors((prev) => ({ ...prev, postalCode: '' }));
              }}
              maxLength={9}
            />
            {holderErrors.postalCode && <p className="mt-1 text-xs text-red-800">{holderErrors.postalCode}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Número</label>
            <input
              key={`addressNumber-${shakeKey}`}
              data-testid="holder-address-number"
              className={getInputClass({ error: holderErrors.addressNumber })}
              placeholder="123"
              value={holderInfo.addressNumber}
              onChange={(e) => {
                setHolderInfo((p) => ({ ...p, addressNumber: e.target.value }));
                if (holderErrors.addressNumber) setHolderErrors((prev) => ({ ...prev, addressNumber: '' }));
              }}
            />
            {holderErrors.addressNumber && <p className="mt-1 text-xs text-red-800">{holderErrors.addressNumber}</p>}
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="holder-info-next"
        onClick={() => handleStepChange('card_data')}
        className="button w-full justify-center"
      >
        Próximo
      </button>
    </>
  );

  const cardDataFields = (
    <>
      {!wide && cardPreview}
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Nome do titular do cartão</label>
          <input
            key={`cardHolder-${shakeKey}`}
            data-testid="card-holder-name"
            className={getInputClass({ error: cardErrors.holderName })}
            placeholder="JOAO FULANO DE TAL MENDES"
            value={card.holderName}
            onChange={(e) => {
              setCard((p) => ({ ...p, holderName: e.target.value }));
              if (cardErrors.holderName) setCardErrors((prev) => ({ ...prev, holderName: '' }));
            }}
          />
          {cardErrors.holderName && <p className="mt-1 text-xs text-red-800">{cardErrors.holderName}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Número do cartão</label>
          <input
            key={`cardNumber-${shakeKey}`}
            data-testid="card-number"
            className={getInputClass({ extra: 'font-mono tracking-widest', error: cardErrors.number })}
            placeholder="0000 0000 0000 0000"
            value={maskCardNumber(card.number)}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
              setCard((p) => ({ ...p, number: digits }));
              if (cardErrors.number) setCardErrors((prev) => ({ ...prev, number: '' }));
              if (digits.length === 16) monthRef.current?.focus();
            }}
            maxLength={19}
          />
          {cardErrors.number && <p className="mt-1 text-xs text-red-800">{cardErrors.number}</p>}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Mês</label>
            <input
              ref={monthRef}
              key={`expiryMonth-${shakeKey}`}
              data-testid="card-expiry-month"
              className={getInputClass({ extra: 'text-center', error: cardErrors.expiryMonth })}
              placeholder="MM"
              value={card.expiryMonth}
              onChange={(e) => {
                const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
                if (raw.length === 2 && Number(raw) > 12) return;
                if (raw.length === 1 && Number(raw) > 1) {
                  const padded = raw.padStart(2, '0');
                  setCard((p) => ({ ...p, expiryMonth: padded }));
                  if (cardErrors.expiryMonth) setCardErrors((prev) => ({ ...prev, expiryMonth: '' }));
                  yearRef.current?.focus();
                  return;
                }
                setCard((p) => ({ ...p, expiryMonth: raw }));
                if (cardErrors.expiryMonth) setCardErrors((prev) => ({ ...prev, expiryMonth: '' }));
                if (raw.length === 2) yearRef.current?.focus();
              }}
              maxLength={2}
            />
            {cardErrors.expiryMonth && <p className="mt-1 text-xs text-red-800">{cardErrors.expiryMonth}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Ano</label>
            <input
              ref={yearRef}
              key={`expiryYear-${shakeKey}`}
              data-testid="card-expiry-year"
              className={getInputClass({ extra: 'text-center', error: cardErrors.expiryYear })}
              placeholder="AA"
              value={card.expiryYear}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                setCard((p) => ({ ...p, expiryYear: digits }));
                if (cardErrors.expiryYear) setCardErrors((prev) => ({ ...prev, expiryYear: '' }));
                if (digits.length === 2) ccvRef.current?.focus();
              }}
              maxLength={2}
            />
            {cardErrors.expiryYear && <p className="mt-1 text-xs text-red-800">{cardErrors.expiryYear}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">CVV</label>
            <input
              ref={ccvRef}
              key={`ccv-${shakeKey}`}
              data-testid="card-ccv"
              className={getInputClass({ extra: 'text-center', error: cardErrors.ccv })}
              placeholder="•••"
              value={card.ccv}
              onChange={(e) => {
                setCard((p) => ({ ...p, ccv: e.target.value.replace(/\D/g, '') }));
                if (cardErrors.ccv) setCardErrors((prev) => ({ ...prev, ccv: '' }));
              }}
              maxLength={4}
            />
            {cardErrors.ccv && <p className="mt-1 text-xs text-red-800">{cardErrors.ccv}</p>}
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="card-data-next"
        onClick={() => handleStepChange('confirm')}
        className="button w-full justify-center"
      >
        Próximo
      </button>
    </>
  );

  const getSubmitLabel = () => {
    if (isPending) return 'Processando...';
    if (submitLabel) return submitLabel;
    if (mode === 'save_card') return 'Salvar cartão';
    const displayCents = discount?.effectiveValueCents ?? valueCents;
    return `Assinar por ${formatPrice({ valueCents: displayCents })}`;
  };

  const confirmFields = (
    <>
      {!wide && cardPreview}
      <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-2">
        {mode === 'subscribe' && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Plano</span>
              <span className="font-semibold text-slate-900">{planName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Valor</span>
              <span className="font-semibold text-slate-900">
                {discount?.effectiveValueCents != null ? formatPrice({ valueCents: discount.effectiveValueCents }) : formatPrice({ valueCents })}
              </span>
            </div>
          </>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Cartão</span>
          <span className="font-semibold text-slate-900">•••• {card.number.slice(-4) || '••••'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Titular</span>
          <span className="font-semibold text-slate-900 truncate ml-4 text-right uppercase">{card.holderName}</span>
        </div>
      </div>
      {error && (
        <p data-testid="card-form-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}
      <button
        type="button"
        data-testid="card-form-submit"
        disabled={isPending}
        onClick={onSubmit}
        className="button w-full justify-center"
      >
        {getSubmitLabel()}
      </button>
    </>
  );

  const stepHeader = (
    <>
      <div className="flex items-center gap-3">
        <BackButton onClick={goBack} />
        <div>
          <p className="font-semibold text-slate-900">Cartão de crédito</p>
          {mode === 'subscribe' && (
            <p className="text-xs text-slate-500">Plano {planName} — {formatPrice({ valueCents })}</p>
          )}
        </div>
      </div>

      <div className="flex items-center">
        {CARD_SUB_STEPS.map((s, i) => (
          <Fragment key={s.id}>
            <button
              type="button"
              onClick={() => handleStepChange(s.id)}
              disabled={!isStepValid(s.id) && i > stepIndex}
              className={getStepButtonClass({ valid: isStepValid(s.id), isForward: i > stepIndex })}
            >
              <div className={getStepBubbleClass(i)} style={getStepBubbleStyle(i)}>
                {i < stepIndex ? (
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l3.5 3.5L13 4.5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span className={getStepLabelClass(i)}>{s.label}</span>
            </button>
            {i < CARD_SUB_STEPS.length - 1 && (
              <div className="mb-4 h-px flex-1 transition-colors" style={getStepConnectorStyle(i)} />
            )}
          </Fragment>
        ))}
      </div>
    </>
  );

  const activeFields = subStep === 'holder_info' ? holderInfoFields : subStep === 'card_data' ? cardDataFields : confirmFields;

  if (wide) {
    return (
      <div className="flex gap-8 py-4">
        <div className="flex flex-1 flex-col space-y-5 min-w-0">
          {stepHeader}
          {activeFields}
        </div>
        <div className="w-72 flex-shrink-0 flex flex-col gap-4 pt-4">
          {cardPreview}
          {mode === 'subscribe' && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Plano</span>
                <span className="font-semibold text-slate-900">{planName}</span>
              </div>
              {valueCents > 0 && discount?.effectiveValueCents != null ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Valor original</span>
                    <span className="font-semibold text-slate-400 line-through">{formatPrice({ valueCents })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Desconto</span>
                    <span className="font-semibold text-green-600">{discount.discountLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700 font-semibold">Valor final</span>
                    <span className="font-bold text-slate-900">{formatPrice({ valueCents: discount.effectiveValueCents })}</span>
                  </div>
                  <p className="text-xs text-slate-500 pt-1">
                    {discount.discountDuration === 'RECURRING' && 'Desconto aplicado em todas as mensalidades'}
                    {discount.discountDuration === 'N_MONTHS' && discount.durationMonths === 1 && `Válido para a 1ª mensalidade · depois ${formatPrice({ valueCents })}`}
                    {discount.discountDuration === 'N_MONTHS' && discount.durationMonths !== 1 && `Válido para as ${discount.durationMonths} primeiras mensalidades · depois ${formatPrice({ valueCents })}`}
                    {(!discount.discountDuration || discount.discountDuration === 'FIRST_ONLY') && `Válido para a 1ª mensalidade · depois ${formatPrice({ valueCents })}`}
                  </p>
                </>
              ) : valueCents > 0 ? (
                <div className="flex justify-between">
                  <span className="text-slate-500">Valor</span>
                  <span className="font-semibold text-slate-900">{formatPrice({ valueCents })}</span>
                </div>
              ) : null}
              {promoCodeSlot && (
                <div className="border-t border-slate-200 pt-2 mt-2">
                  {promoCodeSlot}
                </div>
              )}
            </div>
          )}
          {footer}
          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            Pagamento seguro via Asaas
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl py-4 space-y-5">
      {stepHeader}
      {activeFields}
    </div>
  );
};
